import React, { useEffect, useState } from "react";
import { Send, MapPin, Mail, Phone, Clock, CheckCircle2, MessageCircle } from "lucide-react";
import { site, grades } from "../mock/mock";
import { Eyebrow } from "./SectionHeading";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { createEnquiry, listEnquiries, logContactEvent } from "../lib/api";

const EMPTY = { parent: "", student: "", grade: "", phone: "", email: "", message: "" };
const KEY = "kpr_enquiries";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let alive = true;
    listEnquiries({ limit: 500 })
      .then((rows) => alive && setCount(rows.length))
      .catch(() => {
        try {
          setCount(JSON.parse(localStorage.getItem(KEY) || "[]").length);
        } catch {
          setCount(0);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.parent.trim()) err.parent = "Please enter the parent's name";
    if (!form.student.trim()) err.student = "Please enter the student's name";
    if (!form.grade) err.grade = "Select a grade";
    if (!/^[0-9+\-\s]{10,15}$/.test(form.phone.trim())) err.phone = "Enter a valid phone number";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) err.email = "Enter a valid email";

    if (Object.keys(err).length) {
      setErrors(err);
      toast({ title: "Please check the form", description: "A few fields still need your attention." });
      return;
    }

    setBusy(true);
    try {
      await createEnquiry({ ...form, source: `website:${window.location.pathname}` });
      const rows = await listEnquiries({ limit: 500 }).catch(() => []);
      setCount(rows.length || count + 1);
      setSent(true);
      setForm(EMPTY);
      toast({
        title: "Enquiry received",
        description: "Our admissions team will reach out within one working day.",
      });
    } catch (err) {
      // offline fallback so a parent never loses their details
      try {
        const list = JSON.parse(localStorage.getItem(KEY) || "[]");
        list.push({ ...form, at: new Date().toISOString(), pending: true });
        localStorage.setItem(KEY, JSON.stringify(list));
      } catch {
        /* ignore */
      }
      setSent(true);
      setForm(EMPTY);
      toast({
        title: "Saved on this device",
        description: `We could not reach the server. Please call ${site.hotlines[0]} and we'll take your details.`,
      });
    } finally {
      setBusy(false);
      setTimeout(() => setSent(false), 6000);
    }
  };

  const fieldStyle = (k) => ({
    borderColor: errors[k] ? "#B4453A" : "rgba(11,59,54,.16)",
    background: "#fff",
  });

  return (
    <section id="contact" className="relative overflow-hidden py-24 lg:py-32" style={{ background: "var(--teal-950)" }}>
      <div className="grain absolute inset-0" />
      <div
        className="absolute -right-32 top-10 h-[380px] w-[380px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(192,138,46,.22), transparent 70%)" }}
      />

      <div className="relative mx-auto grid max-w-[1280px] gap-14 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20 lg:px-6">
        {/* Left info */}
        <div data-reveal="">
          <Eyebrow tone="light">Admissions Open 2026–27</Eyebrow>
          <h2
            className="font-display mt-5 text-[2.1rem] leading-[1.1] tracking-tight sm:text-[2.9rem]"
            style={{ color: "#FBF7F0", fontWeight: 300 }}
          >
            Come see the campus for yourself
          </h2>
          <p className="mt-6 text-[1rem] leading-relaxed" style={{ color: "rgba(251,247,240,.75)" }}>
            Tell us a little about your child and we’ll arrange a guided walk-through of the labs,
            studios, library and sports arenas — the parts of KPR you really have to stand inside to understand.
          </p>

          <div className="mt-10 space-y-5">
            {[
              { icon: MapPin, label: "Campus", value: site.address },
              { icon: Phone, label: "Admission hotline", value: site.hotlines.join("  ·  ") },
              { icon: MessageCircle, label: "WhatsApp", value: site.whatsapps.map((w) => w.display).join("  ·  "), href: `https://wa.me/${site.whatsapp}`, log: { channel: "whatsapp", number: site.whatsapp, display: site.whatsappDisplay, label: "contact section" } },
              { icon: Mail, label: "Email", value: site.email, href: `mailto:${site.email}` },
              { icon: Clock, label: "Visiting hours", value: "Mon – Sat · 9:00 AM to 4:00 PM" },
            ].map((r) => (
              <div key={r.label} className="flex gap-4">
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-xl"
                  style={{ background: "rgba(235,214,168,.13)", color: "var(--gold-200)" }}
                >
                  <r.icon size={17} strokeWidth={1.7} />
                </span>
                <div>
                  <p className="text-[0.7rem] tracking-[0.16em] uppercase" style={{ color: "var(--gold-200)" }}>
                    {r.label}
                  </p>
                  {r.href ? (
                    <a
                      href={r.href}
                      onClick={() => r.log && logContactEvent(r.log)}
                      className="mt-1 block text-[0.95rem]"
                      style={{ color: "rgba(251,247,240,.9)" }}
                    >
                      {r.value}
                    </a>
                  ) : (
                    <p className="mt-1 max-w-sm text-[0.95rem] leading-relaxed" style={{ color: "rgba(251,247,240,.9)" }}>
                      {r.value}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {count > 0 && (
            <p className="mt-9 text-[0.78rem]" style={{ color: "rgba(251,247,240,.45)" }}>
              {count} enquir{count > 1 ? "ies" : "y"} received so far.
            </p>
          )}
        </div>

        {/* Form */}
        <div
          className="rounded-[1.5rem] p-7 sm:p-10"
          data-reveal=""
          style={{ background: "var(--cream)", boxShadow: "0 50px 90px -60px rgba(0,0,0,.8)" }}
        >
          <h3 className="font-display text-[1.6rem]" style={{ color: "var(--teal-900)" }}>
            Admission enquiry
          </h3>
          <div className="hairline-gold my-6" />

          {sent && (
            <div
              className="fade-up mb-6 flex items-center gap-3 rounded-xl px-4 py-3.5"
              style={{ background: "var(--teal-100)", color: "var(--teal-800)" }}
            >
              <CheckCircle2 size={18} />
              <span className="text-[0.86rem] font-medium">Thank you — your enquiry has been recorded.</span>
            </div>
          )}

          <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label className="text-[0.78rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                Parent / Guardian name
              </Label>
              <Input
                className="mt-2 h-12"
                style={fieldStyle("parent")}
                placeholder="e.g. Ramya Sundaram"
                value={form.parent}
                onChange={(e) => set("parent", e.target.value)}
              />
              {errors.parent && <p className="mt-1.5 text-[0.74rem]" style={{ color: "#B4453A" }}>{errors.parent}</p>}
            </div>

            <div>
              <Label className="text-[0.78rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                Student name
              </Label>
              <Input
                className="mt-2 h-12"
                style={fieldStyle("student")}
                placeholder="e.g. Aadhya"
                value={form.student}
                onChange={(e) => set("student", e.target.value)}
              />
              {errors.student && <p className="mt-1.5 text-[0.74rem]" style={{ color: "#B4453A" }}>{errors.student}</p>}
            </div>

            <div>
              <Label className="text-[0.78rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                Applying for
              </Label>
              <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
                <SelectTrigger className="mt-2 h-12" style={fieldStyle("grade")}>
                  <SelectValue placeholder="Select grade" />
                </SelectTrigger>
                <SelectContent>
                  {grades.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.grade && <p className="mt-1.5 text-[0.74rem]" style={{ color: "#B4453A" }}>{errors.grade}</p>}
            </div>

            <div>
              <Label className="text-[0.78rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                Phone
              </Label>
              <Input
                className="mt-2 h-12"
                style={fieldStyle("phone")}
                placeholder="+91 90000 00000"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
              {errors.phone && <p className="mt-1.5 text-[0.74rem]" style={{ color: "#B4453A" }}>{errors.phone}</p>}
            </div>

            <div className="sm:col-span-2">
              <Label className="text-[0.78rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                Email
              </Label>
              <Input
                className="mt-2 h-12"
                style={fieldStyle("email")}
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
              {errors.email && <p className="mt-1.5 text-[0.74rem]" style={{ color: "#B4453A" }}>{errors.email}</p>}
            </div>

            <div className="sm:col-span-2">
              <Label className="text-[0.78rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                Anything you’d like us to know (optional)
              </Label>
              <Textarea
                className="mt-2 min-h-[110px]"
                style={fieldStyle("message")}
                placeholder="Questions about transport, sports, timings…"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              className="group mt-2 flex items-center justify-center gap-3 rounded-full py-4 text-[0.88rem] font-semibold sm:col-span-2"
              style={{
                background: busy ? "var(--gold-400)" : "var(--gold)",
                color: "#fff",
                cursor: busy ? "wait" : "pointer",
                transition: "transform .3s ease, box-shadow .3s ease, background .3s ease",
              }}
              onMouseEnter={(e) => {
                if (busy) return;
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 18px 34px -18px rgba(192,138,46,.9)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {busy ? "Sending…" : "Submit Enquiry"}
              <Send size={16} className="group-hover:translate-x-1" style={{ transition: "transform .3s ease" }} />
            </button>

            <p className="text-center text-[0.72rem] sm:col-span-2" style={{ color: "var(--muted-ink)" }}>
              Your details reach our admissions desk directly. We never share them.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
